import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../../api/api';
import { useUserStore } from '../../../stores/userStore';
import useAdminDraftGuard from '../useAdminDraftGuard';

function MergeSession({ sourceAlbumId, impact, disabled, onBusyChange, onMerged }) {
  const [mapping, setMapping] = useState(() =>
    Object.fromEntries(
      impact.tracks.map((track) => [
        track.sourceTrackId,
        track.candidateTrackIds.length === 1 ? String(track.candidateTrackIds[0]) : '',
      ]),
    ),
  );
  const [plan, setPlan] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const alive = useRef(true);
  const pending = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      onBusyChange?.(false);
    };
  }, [onBusyChange]);
  const draft = useAdminDraftGuard(mapping, { enabled: !done, busy });
  async function submit(apply) {
    if (pending.current || disabled || done) return;
    if (
      apply &&
      (!confirmed ||
        !plan?.executionEnabled ||
        plan.blockers.length ||
        !window.confirm(
          '두 리뷰는 보존하고 최근 평가만 집계합니다. 확인한 공식 앨범으로 편입할까요?',
        ))
    )
      return;
    pending.current = true;
    onBusyChange?.(true);
    setBusy(true);
    if (!apply) setPlan(null);
    setError('');
    try {
      const response = await api.postAuth(
        `/admin/direct-catalog/albums/${sourceAlbumId}/canonical/${apply ? 'apply' : 'review'}`,
        {
          targetAlbumId: impact.targetAlbumId,
          tracks: impact.tracks
            .filter((track) => mapping[track.sourceTrackId])
            .map((track) => ({
              sourceTrackId: track.sourceTrackId,
              targetTrackId: Number(mapping[track.sourceTrackId]),
            })),
          ...(apply ? { fingerprint: plan.fingerprint, confirmedSameEdition: true } : {}),
        },
      );
      if (!alive.current) return;
      if (apply) {
        draft.markClean();
        setDone(true);
        setPlan(null);
        onMerged?.();
      } else setPlan(response.data.data);
      setConfirmed(false);
    } catch (e) {
      if (!alive.current) return;
      setError(e.message || '다시 사전검사해주세요.');
      setPlan(null);
      setConfirmed(false);
    } finally {
      if (alive.current) {
        pending.current = false;
        setBusy(false);
        onBusyChange?.(false);
      }
    }
  }
  return (
    <Stack
      spacing={2}
      sx={{
        minWidth: 0,
        overflowWrap: 'anywhere',
        containerType: 'inline-size',
        '@container (max-width: 360px)': {
          '& .MuiAlert-root': { flexDirection: 'column', px: 1 },
          '& .MuiAlert-icon': { pb: 0 },
          '& .MuiFormControlLabel-root': { alignItems: 'flex-start', mr: 0 },
        },
      }}
    >
      <Typography variant="h6">공식 앨범으로 사용자 기록 편입</Typography>
      <Alert severity="info">
        공식 메타데이터는 유지합니다. 기존 리뷰·댓글·좋아요는 보존하고 이전 리뷰를 열람할 수 있게
        하며, 최근 사용자 평가만 평균에 반영합니다. 아래 대응은 추정 후보이므로 원본과 대조해주세요.
      </Alert>
      {impact.tracks.map((track) => (
        <TextField
          select
          SelectProps={{ native: true }}
          key={track.sourceTrackId}
          label={`${track.title} (#${track.sourceTrackId})의 공식 곡`}
          value={mapping[track.sourceTrackId]}
          disabled={disabled || busy || done}
          onChange={(event) => {
            setMapping({ ...mapping, [track.sourceTrackId]: event.target.value });
            setPlan(null);
            setConfirmed(false);
            setError('');
          }}
        >
          <option value="">대응 미확정</option>
          {track.candidateTrackIds.map((id) => (
            <option key={id} value={String(id)}>
              공식 곡 #{id}
            </option>
          ))}
        </TextField>
      ))}
      <Button disabled={disabled || busy || done} onClick={() => submit(false)}>
        기록 편입 사전검사
      </Button>
      {busy && <Typography role="status">기록 편입 처리 중…</Typography>}
      {error && <Alert severity="error">{error}</Alert>}
      {done && (
        <Alert severity="success">
          공식 앨범으로 편입했습니다. 기존 리뷰와 이전 기록을 확인해주세요.
        </Alert>
      )}
      {plan && (
        <>
          {plan.blockers.map((message) => (
            <Alert severity="error" key={message}>
              {message}
            </Alert>
          ))}
          {!plan.executionEnabled && (
            <Alert severity="warning">
              통합·복원 DB 검증 전 실행 잠금 상태입니다. 아직 기록을 변경하지 않았습니다.
            </Alert>
          )}
          <FormControlLabel
            label="동일 발매판·각 곡 대응·두 리뷰 보존과 최근 평가 집계를 확인했습니다."
            control={
              <Checkbox
                checked={confirmed}
                disabled={
                  disabled || busy || !plan.executionEnabled || Boolean(plan.blockers.length)
                }
                onChange={(event) => setConfirmed(event.target.checked)}
              />
            }
          />
          <Button
            variant="contained"
            disabled={
              disabled ||
              busy ||
              !confirmed ||
              !plan.executionEnabled ||
              Boolean(plan.blockers.length)
            }
            onClick={() => submit(true)}
          >
            확인한 기록 편입 실행
          </Button>
        </>
      )}
    </Stack>
  );
}

export default function CanonicalAlbumMerge(props) {
  const user = useUserStore((state) => state.user);
  const scope = JSON.stringify([user?.id, user?.role, props.sourceAlbumId, props.impact]);
  return <MergeSession key={scope} {...props} />;
}
